import React, { useState } from 'react';
import { Section, Card } from './primitives';
import { Segmented, StepRow, Note, SourceRow, ExpandableGroups } from './protocolBits';

/*
 * A reference guide rendered entirely from data, so a hospital can ship a protocol
 * without a component. Shape (see data/hospitals/benTaubOb.js for examples):
 *
 *   guide = { title, intro?, segments?: [{ id, label, sections }], sections?, sources?, collapsible? }
 *   section = { title?, confirm?, blocks: [block] }           (or { title, items } as shorthand for one bullets block)
 *   block   = { type: 'bullets', items }                       plain bullets (item may be string or { text, warn })
 *           | { type: 'steps', items }                         numbered
 *           | { type: 'rows', rows: [{ label, value, note? }] } label left, value right
 *           | { type: 'table', cols, rows: [[...]] }           compact table
 *           | { type: 'note', tone?, text }                    neutral | warn | danger
 *   source  = { title, href, kind?: 'pdf' | 'video', detail? }
 *
 * `collapsible: true` renders the sections as collapsed rows that open in place.
 */

const Confirm = () => (
  <span className="inline-block ml-1.5 align-middle px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
    confirm
  </span>
);

const Steps = ({ items, accent }) => items.map((step, i) => (
  <div key={typeof step === 'string' ? step : step.text} className="px-4 py-2.5 flex gap-3">
    <span className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold flex-shrink-0" style={{ backgroundColor: `${accent}24`, color: accent }}>{i + 1}</span>
    <span className="text-sm text-gray-900 dark:text-white leading-snug pt-0.5">{typeof step === 'string' ? step : step.text}</span>
  </div>
));

const Rows = ({ rows }) => rows.map((r) => (
  <div key={r.label} className="px-4 py-2.5 flex items-baseline justify-between gap-4">
    <span className="text-sm text-gray-600 dark:text-gray-300">{r.label}</span>
    <span className="text-right">
      <span className="text-[15px] font-semibold text-gray-900 dark:text-white">{r.value}</span>
      {r.note && <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">{r.note}</span>}
    </span>
  </div>
));

const Table = ({ cols, rows }) => (
  <div className="px-4 py-2 overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-500 dark:text-gray-400">
          {cols.map((c, i) => <th key={c} className={`py-1 font-medium ${i ? 'pl-2' : ''}`}>{c}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
        {rows.map((row) => (
          <tr key={row.join('|')}>
            {row.map((cell, i) => <td key={i} className={`py-1.5 align-top leading-snug ${i ? 'pl-2 text-gray-700 dark:text-gray-300' : 'font-medium text-gray-900 dark:text-white'}`}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Block = ({ block, accent }) => {
  switch (block.type) {
    case 'steps': return <Steps items={block.items} accent={accent} />;
    case 'rows': return <Rows rows={block.rows} />;
    case 'table': return <Table cols={block.cols} rows={block.rows} />;
    case 'note': return <Note tone={block.tone}>{block.text}</Note>;
    default: return <StepRow title={block.title} items={block.items} />;
  }
};

const normalise = (section) => (section.blocks ? section : { ...section, blocks: [{ type: 'bullets', items: section.items || [] }] });

const SectionCard = ({ section, accent }) => {
  const s = normalise(section);
  return (
    <Card>
      {s.blocks.map((b, i) => <Block key={i} block={b} accent={accent} />)}
    </Card>
  );
};

const Sections = ({ sections, accent, collapsible }) => {
  if (collapsible) {
    return (
      <ExpandableGroups
        groups={sections.map((s) => ({
          id: s.title,
          title: s.title,
          danger: s.danger,
          content: normalise(s).blocks.map((b, i) => <Block key={i} block={b} accent={accent} />),
        }))}
      />
    );
  }
  return sections.map((s, i) => (
    <Section key={s.title || i} title={s.title ? <>{s.title}{s.confirm && <Confirm />}</> : undefined}>
      <SectionCard section={s} accent={accent} />
    </Section>
  ));
};

const GuideScreen = ({ guide, accent }) => {
  const [seg, setSeg] = useState(guide.segments?.[0]?.id);
  const current = guide.segments?.find((s) => s.id === seg);
  const sections = current ? current.sections : guide.sections || [];
  return (
    <div className="space-y-6">
      {guide.segments && <Segmented items={guide.segments} value={seg} onChange={setSeg} />}
      {guide.intro && <p className="px-1 text-sm text-gray-600 dark:text-gray-300 leading-snug">{guide.intro}</p>}
      {current?.intro && <p className="px-1 text-sm text-gray-600 dark:text-gray-300 leading-snug">{current.intro}</p>}
      <Sections sections={sections} accent={accent} collapsible={current?.collapsible ?? guide.collapsible} />
      {guide.sources?.length > 0 && (
        <Section title="Resources">
          <div className="space-y-3">
            {guide.sources.map((s) => <SourceRow key={s.title} href={s.href} title={s.title} detail={s.detail} kind={s.kind} accent={accent} />)}
          </div>
        </Section>
      )}
    </div>
  );
};

export default GuideScreen;
